import React, { useState } from 'react';
import { Button, Modal } from 'react-bootstrap';

const LEVEL_CONFIG = {
  major: { color: '#dc3545', label: 'Major' },
  moderate: { color: '#fd7e14', label: 'Moderate' },
  minor: { color: '#ffc107', label: 'Minor' },
};

const LEVEL_ORDER = ['major', 'moderate', 'minor'];

const norm = (s) => (s || '').toLowerCase().trim();

const pairKey = (a, b, level) =>
  [norm(a), norm(b)].sort().join('|') + '|' + norm(level);

const sameGeneric = (a, b) => {
  const na = norm(a);
  const nb = norm(b);
  return na === nb || na.includes(nb) || nb.includes(na);
};

const getOther = (inter, medicineGenericName) =>
  sameGeneric(inter.genericName, medicineGenericName)
    ? inter.genericNameInteraction
    : inter.genericName;

const WarningIcon = ({ color, onClick, title }) => (
  <span
    title={title}
    onClick={onClick}
    style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', marginRight: '3px' }}
  >
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill={color}
      stroke={color}
      strokeWidth="1.3"
      strokeLinejoin="round"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M12 2L1 21h22L12 2zm0 3.5L21 20H3L12 5.5zM11 10v4h2v-4h-2zm0 6v2h2v-2h-2z" />
    </svg>
  </span>
);

function DrugInteractionAlert({
  medicine,
  allGenericNames,
  interactions,
  acknowledgedPairs,
  onAcknowledge,
}) {
  const [modalLevel, setModalLevel] = useState(null);

  if (!medicine?.genericName || !interactions?.length) return null;

  // Group active interactions by severity level
  const activeByLevel = {};
  LEVEL_ORDER.forEach((level) => {
    const items = interactions.filter((inter) => {
      const otherGeneric = getOther(inter, medicine.genericName);
      return (
        norm(inter.level) === level &&
        allGenericNames.some((n) => sameGeneric(n, otherGeneric)) &&
        !acknowledgedPairs.has(pairKey(medicine.genericName, otherGeneric, level))
      );
    });
    if (items.length > 0) activeByLevel[level] = items;
  });

  const levelsToShow = LEVEL_ORDER.filter((l) => activeByLevel[l]).slice(0, 3);
  if (levelsToShow.length === 0) return null;

  // All interactions for the clicked level (may include multiple other medicines)
  const modalInteractions = modalLevel ? activeByLevel[modalLevel] || [] : [];

  const handleAcknowledge = () => {
    const keys = modalInteractions.map((inter) => {
      const otherGeneric = getOther(inter, medicine.genericName);
      return pairKey(medicine.genericName, otherGeneric, modalLevel);
    });
    onAcknowledge(keys, modalInteractions);
    setModalLevel(null);
  };

  return (
    <>
      <span
        data-interaction-alert="true"
        onClick={(e) => e.stopPropagation()}
        style={{ display: 'inline' }}
      >
        {levelsToShow.map((level) => (
          <WarningIcon
            key={level}
            color={LEVEL_CONFIG[level].color}
            title={`${LEVEL_CONFIG[level].label} interaction — click for details`}
            onClick={(e) => {
              e.stopPropagation();
              setModalLevel(level);
            }}
          />
        ))}
      </span>

      <Modal show={!!modalLevel} onHide={() => setModalLevel(null)} centered>
        <Modal.Header
          className="common-modal-header"
          style={{ borderBottom: `3px solid ${LEVEL_CONFIG[modalLevel]?.color}` }}
        >
          <Modal.Title style={{ fontSize: '1rem', color: LEVEL_CONFIG[modalLevel]?.color }}>
            {LEVEL_CONFIG[modalLevel]?.label} Drug Interaction — {medicine.genericName}
          </Modal.Title>
          <i
            className="far fa-times-circle cursor-pointer text-danger"
            onClick={() => setModalLevel(null)}
            aria-hidden="true"
          />
        </Modal.Header>
        <Modal.Body>
          {modalInteractions.map((inter, i) => {
            const otherGeneric = getOther(inter, medicine.genericName);
            return (
              <div
                key={i}
                style={{
                  marginBottom: '10px',
                  padding: '10px 14px',
                  background: '#fff8f8',
                  borderRadius: '6px',
                  border: `1px solid ${LEVEL_CONFIG[modalLevel]?.color}44`,
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                  <span style={{ color: LEVEL_CONFIG[modalLevel]?.color }}>
                    {medicine.genericName}
                  </span>
                  {' + '}
                  <span style={{ color: LEVEL_CONFIG[modalLevel]?.color }}>
                    {otherGeneric}
                  </span>
                </div>
                {inter.effect && (
                  <p style={{ margin: 0, fontSize: '0.88rem', color: '#555' }}>
                    {inter.effect}
                  </p>
                )}
              </div>
            );
          })}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" onClick={() => setModalLevel(null)}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={handleAcknowledge}>
            Acknowledged
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default DrugInteractionAlert;
